import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play,
} from "lucide-react";
import { Field } from "../shared.jsx";

const SECTION_LABELS = {
    home_hero: "Homepage hero",
    home_intro: "Homepage 'love letter' intro",
    home_cta_divider: "Homepage subscribe banner",
    about_hero: "About page header",
    about_story: "About page story",
    about_kit: "About page — the kit list",
    subscription_copy: "Subscription page copy",
    contact_copy: "Contact page copy",
};

export default function ContentEditor() {
    const [sections, setSections] = useState([]);
    const [active, setActive] = useState(null);

    useEffect(() => {
        api.get("/content").then((r) => {
            setSections(r.data);
            setActive(r.data[0]?.section || null);
        });
    }, []);

    const update = (section, data) => {
        setSections((s) => s.map((x) => x.section === section ? { ...x, data } : x));
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <aside className="lg:col-span-3 space-y-1" data-testid="content-section-list">
                {sections.map((s) => (
                    <button
                        key={s.section}
                        onClick={() => setActive(s.section)}
                        className={`w-full text-left px-4 py-3 text-sm border-l-2 transition-colors ${
                            active === s.section
                                ? "border-[var(--sb-brand)] bg-[var(--sb-surface)] text-[var(--sb-text)]"
                                : "border-transparent text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-surface)]"
                        }`}
                        data-testid={`content-section-${s.section}`}
                    >
                        {SECTION_LABELS[s.section] || s.section}
                    </button>
                ))}
            </aside>
            <div className="lg:col-span-9">
                {active && (
                    <SectionEditor
                        key={active}
                        section={sections.find((s) => s.section === active)}
                        onChange={(d) => update(active, d)}
                    />
                )}
            </div>
        </div>
    );
}

function SectionEditor({ section, onChange }) {
    const [draft, setDraft] = useState(section.data);
    const [busy, setBusy] = useState(false);

    useEffect(() => { setDraft(section.data); }, [section]);

    const setField = (key, value) => {
        const next = { ...draft, [key]: value };
        setDraft(next);
    };

    const setListItem = (key, i, value) => {
        const list = [...(draft[key] || [])];
        list[i] = value;
        setField(key, list);
    };

    const addListItem = (key) => {
        const list = [...(draft[key] || []), ""];
        setField(key, list);
    };

    const removeListItem = (key, i) => {
        const list = (draft[key] || []).filter((_, idx) => idx !== i);
        setField(key, list);
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/content/${section.section}`, { data: draft });
            onChange(draft);
            clearContentCache();
            toast.success("Saved.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Reset to the original default copy? Your edits will be lost.")) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/content/${section.section}/reset`);
            setDraft(data.data);
            onChange(data.data);
            clearContentCache();
            toast.success("Reset to defaults.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card p-6 lg:p-8" data-testid="section-editor">
            <div className="flex items-center justify-between gap-4 mb-6">
                <h2 className="font-serif text-2xl tracking-tight">{SECTION_LABELS[section.section] || section.section}</h2>
                <div className="flex gap-2">
                    <button onClick={reset} disabled={busy} className="btn-outline text-sm" style={{padding: "0.55rem 1rem"}} data-testid="section-reset">
                        <RotateCcw size={14} /> Reset
                    </button>
                    <button onClick={save} disabled={busy} className="btn-primary text-sm" style={{padding: "0.55rem 1rem"}} data-testid="section-save">
                        <Save size={14} /> {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>

            <div className="space-y-6">
                {Object.entries(draft).map(([key, value]) => (
                    <div key={key}>
                        <label className="overline block mb-2">{key.replace(/_/g, " ")}</label>
                        {Array.isArray(value) ? (
                            <div className="space-y-2" data-testid={`field-list-${key}`}>
                                {value.map((item, i) => (
                                    <div key={i} className="flex gap-2 items-start">
                                        <textarea
                                            value={item}
                                            onChange={(e) => setListItem(key, i, e.target.value)}
                                            rows={Math.max(1, Math.ceil((item || "").length / 80))}
                                            className="input-line flex-1"
                                            data-testid={`field-list-${key}-${i}`}
                                        />
                                        <button onClick={() => removeListItem(key, i)} className="text-[var(--sb-text-muted)] hover:text-red-400 p-2" data-testid={`field-list-remove-${key}-${i}`}>
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                                <button onClick={() => addListItem(key)} className="text-sm text-[var(--sb-brand)] hover:text-[var(--sb-text)] inline-flex items-center gap-1" data-testid={`field-list-add-${key}`}>
                                    <Plus size={14} /> Add item
                                </button>
                            </div>
                        ) : (value || "").length > 80 ? (
                            <textarea
                                value={value}
                                onChange={(e) => setField(key, e.target.value)}
                                rows={Math.max(3, Math.ceil(value.length / 80))}
                                className="input-line"
                                data-testid={`field-${key}`}
                            />
                        ) : (
                            <input
                                type="text"
                                value={value}
                                onChange={(e) => setField(key, e.target.value)}
                                className="input-line"
                                data-testid={`field-${key}`}
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Videos editor                                                              */
